// File: UndergradAdmissionApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function UndergradAdmissionApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Application basics
    startTerm: '',
    entryType: 'First-time Freshman', // First-time Freshman | Transfer | International | Returning | Non-Degree
    intendedCollege: '',
    intendedMajor: '',
    secondChoiceMajor: '',

    // Personal info
    legalFirst: '',
    legalMiddle: '',
    legalLast: '',
    preferredName: '',
    dob: '',
    gender: '',
    ssnLast4: '',

    // Contact
    email: '',
    mobile: '',
    allowText: true,

    // Address
    homeStreet: '',
    homeApt: '',
    homeCity: '',
    homeState: '',
    homeZip: '',
    homeCounty: '',
    mailingSameAsHome: true,
    mailingStreet: '',
    mailingApt: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',

    // Residency & citizenship
    gtsResident: '',
    yearsInGTS: '',
    citizenship: '',
    countryCitizenship: '',
    visaType: '',

    // Demographics (optional)
    ethnicity: '',
    raceWhite: false,
    raceBlack: false,
    raceAsian: false,
    raceNHOPI: false,
    raceAIAN: false,
    raceOther: false,
    raceOtherText: '',
    firstGen: '', // Yes|No

    // High school (required)
    hsName: '',
    hsCity: '',
    hsState: '',
    hsGradDate: '',
    hsGPA: '',
    classRank: '',
    classSize: '',

    // Testing (optional)
    testOptional: true,
    satTotal: '',
    actComposite: '',
    apIbCredits: '',

    // Housing & aid
    housingPreference: '',
    applyFinancialAid: 'Yes', // Yes | No
    honorsInterest: 'No',

    // Disciplinary history (simple Yes/No, optional explanation)
    academicDiscipline: '',
    academicDisciplineExplain: '',

    // Emergency contact
    emergencyName: '',
    emergencyRelation: '',
    emergencyPhone: '',
    emergencyEmail: '',

    // Short answers (optional)
    activitiesHonors: '',
    personalStatement: '',

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  // Dynamic lists
  const [colleges, setColleges] = useState([
    { name: '', city: '', state: '', dates: '', creditsEarned: '', gpa: '' }
  ]);

  // Helpers
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'mailingSameAsHome') {
      const next = checked;
      setForm((f) => {
        const updated = { ...f, mailingSameAsHome: next };
        if (next) {
          updated.mailingStreet = f.homeStreet;
          updated.mailingApt = f.homeApt;
          updated.mailingCity = f.homeCity;
          updated.mailingState = f.homeState.toUpperCase();
          updated.mailingZip = f.homeZip;
        }
        return updated;
      });
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const updateRow = (rows, setRows, idx, key, value) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: value };
    setRows(copy);
  };
  const addRow = (rows, setRows, blank) => setRows([...rows, { ...blank }]);
  const removeRow = (rows, setRows, idx, blank) => {
    const copy = [...rows];
    copy.splice(idx, 1);
    setRows(copy.length ? copy : [{ ...blank }]);
  };

  // Validation
  const validate = () => {
    const e = {};
    // Only require the first field (legalFirst) for demo purposes
    if (!String(form.legalFirst || '').trim()) {
      e.legalFirst = 'Required';
    }

    // Keep format validation for fields that are filled in (but don't require them)
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    if (form.mobile && !isTenPhone(form.mobile)) e.mobile = '10 digits only';

    if (form.homeState && !isTwoLetterState(form.homeState)) e.homeState = 'Use 2-letter code';
    if (form.homeZip && !isFiveZip(form.homeZip)) e.homeZip = '5-digit ZIP';

    if (!form.mailingSameAsHome) {
      if (form.mailingState && !isTwoLetterState(form.mailingState)) e.mailingState = 'Use 2-letter code';
      if (form.mailingZip && !isFiveZip(form.mailingZip)) e.mailingZip = '5-digit ZIP';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 500);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">GTS State University — Undergraduate Application</h4>
              <p className="gts-form-overview">
                Apply for freshman or transfer admission to GTSU. This is a <strong>demo-only</strong> submission—no data is stored or transmitted.
              </p>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions">
                  <button className="btn btn-secondary" onClick={() => window.print()}>Print this page</button>
                  <Link to="/forms" className="btn btn-secondary">Back to Forms &amp; Applications</Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* App Basics */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Application Basics</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Start Term <span className="gts-required">*</span></span>
                      <select name="startTerm" value={form.startTerm} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Fall 2025</option>
                        <option>Spring 2026</option>
                        <option>Summer 2026</option>
                      </select>
                      {errors.startTerm && <em className="gts-error">{errors.startTerm}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Entry Type <span className="gts-required">*</span></span>
                      <select name="entryType" value={form.entryType} onChange={onChange}>
                        <option>First-time Freshman</option>
                        <option>Transfer</option>
                        <option>International</option>
                        <option>Returning</option>
                        <option>Non-Degree</option>
                      </select>
                      {errors.entryType && <em className="gts-error">{errors.entryType}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Intended College</span>
                      <select name="intendedCollege" value={form.intendedCollege} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Arts &amp; Sciences</option>
                        <option>Engineering &amp; Computing</option>
                        <option>Business</option>
                        <option>Education</option>
                        <option>Health Sciences</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Intended Major <span className="gts-required">*</span></span>
                      <input name="intendedMajor" value={form.intendedMajor} onChange={onChange} placeholder="e.g., Computer Science" />
                      {errors.intendedMajor && <em className="gts-error">{errors.intendedMajor}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Second-choice Major (optional)</span>
                      <input name="secondChoiceMajor" value={form.secondChoiceMajor} onChange={onChange} />
                    </label>
                  </div>
                </div>

                {/* Personal */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Personal Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Legal First Name <span className="gts-required">*</span></span>
                      <input name="legalFirst" value={form.legalFirst} onChange={onChange} />
                      {errors.legalFirst && <em className="gts-error">{errors.legalFirst}</em>}
                    </label>
                    <label className="gts-field"><span>Middle</span><input name="legalMiddle" value={form.legalMiddle} onChange={onChange} /></label>
                    <label className="gts-field">
                      <span>Legal Last Name <span className="gts-required">*</span></span>
                      <input name="legalLast" value={form.legalLast} onChange={onChange} />
                      {errors.legalLast && <em className="gts-error">{errors.legalLast}</em>}
                    </label>

                    <label className="gts-field"><span>Preferred Name</span><input name="preferredName" value={form.preferredName} onChange={onChange} /></label>
                    <label className="gts-field">
                      <span>Date of Birth <span className="gts-required">*</span></span>
                      <input type="date" name="dob" value={form.dob} onChange={onChange} />
                      {errors.dob && <em className="gts-error">{errors.dob}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Gender</span>
                      <select name="gender" value={form.gender} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Female</option>
                        <option>Male</option>
                        <option>Non-binary</option>
                        <option>Prefer not to answer</option>
                        <option>Other / Self-describe</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>SSN (last 4, optional)</span>
                      <input name="ssnLast4" value={form.ssnLast4} onChange={onChange} maxLength={4} />
                    </label>
                  </div>
                </div>

                {/* Contact & Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Contact &amp; Address</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Email <span className="gts-required">*</span></span>
                      <input type="email" name="email" value={form.email} onChange={onChange} />
                      {errors.email && <em className="gts-error">{errors.email}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Mobile Phone</span>
                      <input name="mobile" value={form.mobile} onChange={onChange} maxLength={14} />
                      {errors.mobile && <em className="gts-error">{errors.mobile}</em>}
                    </label>

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="allowText" checked={form.allowText} onChange={onChange} />
                      <span>Okay to text me about application status and deadlines</span>
                    </label>

                    <label className="gts-field gts-span-2">
                      <span>Permanent Street <span className="gts-required">*</span></span>
                      <input name="homeStreet" value={form.homeStreet} onChange={onChange} />
                    </label>
                    <label className="gts-field"><span>Apt/Suite</span><input name="homeApt" value={form.homeApt} onChange={onChange} /></label>
                    <label className="gts-field"><span>City <span className="gts-required">*</span></span><input name="homeCity" value={form.homeCity} onChange={onChange} /></label>
                    <label className="gts-field">
                      <span>State (2) <span className="gts-required">*</span></span>
                      <input name="homeState" value={form.homeState} onChange={onChange} maxLength={2} placeholder="TN" />
                      {errors.homeState && <em className="gts-error">{errors.homeState}</em>}
                    </label>
                    <label className="gts-field">
                      <span>ZIP (5) <span className="gts-required">*</span></span>
                      <input name="homeZip" value={form.homeZip} onChange={onChange} maxLength={5} />
                      {errors.homeZip && <em className="gts-error">{errors.homeZip}</em>}
                    </label>
                    <label className="gts-field"><span>County</span><input name="homeCounty" value={form.homeCounty} onChange={onChange} /></label>
                    {errors.address && <em className="gts-error">{errors.address}</em>}

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="mailingSameAsHome" checked={form.mailingSameAsHome} onChange={onChange} />
                      <span>Mailing address is the same as permanent</span>
                    </label>

                    {!form.mailingSameAsHome && (
                      <>
                        <label className="gts-field gts-span-2"><span>Mailing Street</span><input name="mailingStreet" value={form.mailingStreet} onChange={onChange} /></label>
                        <label className="gts-field"><span>Apt/Suite</span><input name="mailingApt" value={form.mailingApt} onChange={onChange} /></label>
                        <label className="gts-field"><span>City</span><input name="mailingCity" value={form.mailingCity} onChange={onChange} /></label>
                        <label className="gts-field"><span>State (2)</span><input name="mailingState" value={form.mailingState} onChange={onChange} maxLength={2} /></label>
                        {errors.mailingState && <em className="gts-error">{errors.mailingState}</em>}
                        <label className="gts-field"><span>ZIP (5)</span><input name="mailingZip" value={form.mailingZip} onChange={onChange} maxLength={5} /></label>
                        {errors.mailingZip && <em className="gts-error">{errors.mailingZip}</em>}
                      </>
                    )}
                  </div>
                </div>

                {/* Residency & Citizenship */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Residency &amp; Citizenship</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Are you a resident of the State of GTS?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}><input type="radio" name="gtsResident" value={o} checked={form.gtsResident===o} onChange={onChange} /><span>{o}</span></label>
                        ))}
                      </div>
                    </fieldset>
                    {form.gtsResident === 'Yes' && (
                      <label className="gts-field"><span>Years in GTS</span><input name="yearsInGTS" value={form.yearsInGTS} onChange={onChange} /></label>
                    )}

                    <label className="gts-field">
                      <span>Citizenship</span>
                      <select name="citizenship" value={form.citizenship} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>U.S. Citizen</option>
                        <option>Permanent Resident</option>
                        <option>Non-U.S. Citizen</option>
                      </select>
                    </label>
                    {form.citizenship && form.citizenship !== 'U.S. Citizen' && (
                      <>
                        <label className="gts-field"><span>Country of Citizenship</span><input name="countryCitizenship" value={form.countryCitizenship} onChange={onChange} /></label>
                        <label className="gts-field"><span>Current Visa Type (if any)</span><input name="visaType" value={form.visaType} onChange={onChange} /></label>
                      </>
                    )}
                  </div>
                </div>

                {/* Demographics (optional) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Demographics (Optional)</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Hispanic/Latino</span>
                      <select name="ethnicity" value={form.ethnicity} onChange={onChange}>
                        <option value="">Prefer not to answer</option>
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </label>

                    <fieldset className="gts-field gts-span-2">
                      <legend>Race (check all that apply)</legend>
                      <div className="gts-radio-list">
                        <label className="gts-radio"><input type="checkbox" name="raceWhite" checked={form.raceWhite} onChange={onChange} /><span>White</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceBlack" checked={form.raceBlack} onChange={onChange} /><span>Black/African American</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceAsian" checked={form.raceAsian} onChange={onChange} /><span>Asian</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceNHOPI" checked={form.raceNHOPI} onChange={onChange} /><span>Native Hawaiian/Other Pacific Islander</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceAIAN" checked={form.raceAIAN} onChange={onChange} /><span>American Indian/Alaska Native</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceOther" checked={form.raceOther} onChange={onChange} /><span>Other</span></label>
                      </div>
                      {form.raceOther && (
                        <div style={{ marginTop: 8 }}>
                          <input name="raceOtherText" value={form.raceOtherText} onChange={onChange} placeholder="Describe 'Other' race" />
                        </div>
                      )}
                    </fieldset>

                    <label className="gts-field">
                      <span>First-generation college student?</span>
                      <select name="firstGen" value={form.firstGen} onChange={onChange}>
                        <option value="">Prefer not to answer</option>
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* High School */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>High School Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>High School <span className="gts-required">*</span></span>
                      <input name="hsName" value={form.hsName} onChange={onChange} />
                      {errors.hsName && <em className="gts-error">{errors.hsName}</em>}
                    </label>
                    <label className="gts-field"><span>City</span><input name="hsCity" value={form.hsCity} onChange={onChange} /></label>
                    <label className="gts-field"><span>State (2)</span><input name="hsState" value={form.hsState} onChange={onChange} maxLength={2} /></label>
                    <label className="gts-field">
                      <span>Graduation Date <span className="gts-required">*</span></span>
                      <input type="date" name="hsGradDate" value={form.hsGradDate} onChange={onChange} />
                      {errors.hsGradDate && <em className="gts-error">{errors.hsGradDate}</em>}
                    </label>
                    <label className="gts-field"><span>Unweighted GPA (4.0)</span><input name="hsGPA" value={form.hsGPA} onChange={onChange} /></label>
                    <label className="gts-field"><span>Class Rank</span><input name="classRank" value={form.classRank} onChange={onChange} placeholder="e.g., 45" /></label>
                    <label className="gts-field"><span>Class Size</span><input name="classSize" value={form.classSize} onChange={onChange} placeholder="e.g., 300" /></label>
                  </div>
                </div>

                {/* Testing */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Testing</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="testOptional" checked={form.testOptional} onChange={onChange} />
                      <span>Apply test-optional (do not consider standardized test scores)</span>
                    </label>

                    {!form.testOptional && (
                      <>
                        <label className="gts-field"><span>SAT Total</span><input name="satTotal" value={form.satTotal} onChange={onChange} /></label>
                        <label className="gts-field"><span>ACT Composite</span><input name="actComposite" value={form.actComposite} onChange={onChange} /></label>
                      </>
                    )}

                    <label className="gts-field gts-span-2">
                      <span>AP/IB/Dual Enrollment credits (optional)</span>
                      <input name="apIbCredits" value={form.apIbCredits} onChange={onChange} placeholder="e.g., AP Calculus (5), IB Biology (HL 6)" />
                    </label>
                  </div>
                </div>

                {/* Prior Colleges (for transfers/dual) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Prior Colleges (if any)</h5><div className="gts-small-note">Transfers list every college attended.</div></div>
                  <div className="gts-panel">
                    {colleges.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field gts-span-2"><span>Institution</span><input value={row.name||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'name',e.target.value)} /></label>
                        <label className="gts-field"><span>City</span><input value={row.city||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'city',e.target.value)} /></label>
                        <label className="gts-field"><span>State</span><input value={row.state||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'state',e.target.value)} maxLength={2} /></label>
                        <label className="gts-field"><span>Dates Attended</span><input value={row.dates||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'dates',e.target.value)} placeholder="e.g., 08/2023–05/2024" /></label>
                        <label className="gts-field"><span>Credits Earned</span><input value={row.creditsEarned||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'creditsEarned',e.target.value)} /></label>
                        <label className="gts-field"><span>College GPA</span><input value={row.gpa||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'gpa',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(colleges,setColleges,idx,{ name:'',city:'',state:'',dates:'',creditsEarned:'',gpa:'' })}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(colleges,setColleges,{ name:'',city:'',state:'',dates:'',creditsEarned:'',gpa:'' })}>Add College</button>
                    </div>
                  </div>
                </div>

                {/* Activities & Essay */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Activities &amp; Personal Statement</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>Activities, leadership, service (optional)</span>
                      <textarea name="activitiesHonors" rows={4} value={form.activitiesHonors} onChange={onChange} placeholder="Clubs, sports, employment, community service, honors & awards" />
                    </label>
                    <label className="gts-field gts-span-2">
                      <span>Personal statement (optional)</span>
                      <textarea name="personalStatement" rows={6} value={form.personalStatement} onChange={onChange} placeholder="Share context, goals, or any information you want the committee to know." />
                    </label>
                  </div>
                </div>

                {/* Housing & Aid */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Housing &amp; Financial Aid</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Housing Preference</span>
                      <select name="housingPreference" value={form.housingPreference} onChange={onChange}>
                        <option value="">Undecided</option>
                        <option>On-campus</option>
                        <option>Off-campus</option>
                        <option>With family/guardian</option>
                      </select>
                    </label>

                    <fieldset className="gts-field">
                      <legend>Plan to apply for financial aid (FAFSA)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="applyFinancialAid" value={o} checked={form.applyFinancialAid===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Interested in Honors?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="honorsInterest" value={o} checked={form.honorsInterest===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Conduct */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Academic Conduct</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Have you ever been disciplined for academic misconduct?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="academicDiscipline" value={o} checked={form.academicDiscipline===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.academicDiscipline === 'Yes' && (
                      <label className="gts-field gts-span-2">
                        <span>Please explain (brief)</span>
                        <textarea name="academicDisciplineExplain" rows={3} value={form.academicDisciplineExplain} onChange={onChange} />
                      </label>
                    )}
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Emergency Contact</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Name</span><input name="emergencyName" value={form.emergencyName} onChange={onChange} /></label>
                    <label className="gts-field"><span>Relationship</span><input name="emergencyRelation" value={form.emergencyRelation} onChange={onChange} /></label>
                    <label className="gts-field"><span>Phone</span><input name="emergencyPhone" value={form.emergencyPhone} onChange={onChange} maxLength={14} /></label>
                    <label className="gts-field"><span>Email</span><input name="emergencyEmail" value={form.emergencyEmail} onChange={onChange} /></label>
                  </div>
                </div>

                {/* Declaration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange} />
                      <span>
                        I certify the information provided is true and complete. I understand this is a demo and no payment or official submission is processed.
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Applicant Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange} />
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange} />
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: 5 }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default UndergradAdmissionApplication;
